import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import stripeCheckoutService from '../../../../api/services/checkout';
import StripeContainer from './FormContainer';
import { Close } from '@mui/icons-material';
import { listings, ShowingType } from '@prisma/client';
import { getStripe } from '../../../../utils/stripeLoader';
import useIsWaitlisted from '../../../../hooks/useIsWaitlisted';
import useToursPaused from '../../../../hooks/useToursPaused';
import WaitlistNotice from '../../../../components/WaitlistNotice/WaitlistNotice';

interface Props {
  listing: listings;
  numDays: number;
  showingType?: ShowingType;
  isFinalWalkthrough?: boolean;
}

const ScheduleTourModal = NiceModal.create<Props>((props) => {
  const modal = useModal();
  const { listing, numDays, showingType, isFinalWalkthrough = false } = props;
  // Memoized inside getStripe, so Elements sees the same promise every render.
  const stripePromise = getStripe();
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isWaitlisted = useIsWaitlisted();
  const toursPaused = useToursPaused();
  // Only plain tours are paused — final walkthroughs and other showing types
  // belong to in-flight transactions and stay open.
  const isTourRequest =
    !isFinalWalkthrough && (!showingType || showingType === ShowingType.TOUR);

  // COMMENTED OUT: Tour fee payment - tours are now free
  // To restore $50 tour fee, uncomment this block and the Stripe payment step in FormContainer
  // const getClientSecret = async () => {
  //   setLoading(true);
  //   const res = await stripeCheckoutService.post({ amount: 5000 });
  //   setClientSecret(res.clientSecret);
  //   setLoading(false);
  // };

  // useEffect(() => {
  //   // Only get client secret if not a final walkthrough (which is free)
  //   if (!isFinalWalkthrough) {
  //     getClientSecret();
  //   }
  // }, [isFinalWalkthrough]);

  const appearance = {
    theme: 'stripe',
  };
  const options = {
    clientSecret,
    appearance: appearance as any,
  };

  // For final walkthroughs, provide minimal Stripe options without a clientSecret
  const finalWalkthroughOptions = {
    mode: 'setup' as const,
    currency: 'usd',
    appearance: appearance as any,
  };

  // COMMENTED OUT: Loading state for Stripe - tours are now free
  // if (loading) {
  //   return <CircularProgress />;
  // }

  if (isWaitlisted || (toursPaused && isTourRequest)) {
    return (
      <Dialog
        open={modal.visible}
        onClose={() => modal.remove()}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogContent>
          {isWaitlisted ? (
            <WaitlistNotice variant="compact" />
          ) : (
            <WaitlistNotice
              variant="compact"
              title="Tours are paused"
              body={
                "Homepoint is currently at capacity — tour scheduling is temporarily paused. " +
                "We'll email you the moment tours reopen. In the meantime, feel free to keep " +
                'browsing listings and exploring the market.'
              }
            />
          )}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.remove()}
      maxWidth="md"
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography variant="h6" fontWeight={600}>
            {showingType === ShowingType.FINAL_WALKTHROUGH
              ? 'Schedule Final Walkthrough'
              : 'Schedule Tour'}
          </Typography>
          <IconButton onClick={() => modal.remove()}>
            <Close />
          </IconButton>
        </Stack>
        <Typography variant="body2">
          {isFinalWalkthrough
            ? 'Final walkthroughs are free for Homepoint buyers.'
            : 'Tours are free with Homepoint!'}
          {/* COMMENTED OUT: $50 tour fee message
            : 'Tours are $50 per tour. If you close on a home, any tour fees will be credited.'
          */}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* COMMENTED OUT: Stripe payment conditional - tours are now free
        {(clientSecret !== '' || isFinalWalkthrough) && (
          <Elements
            options={isFinalWalkthrough ? finalWalkthroughOptions : options}
            stripe={stripePromise}
          >
        */}
        <Elements
          options={finalWalkthroughOptions}
          stripe={stripePromise}
        >
          {/* Tours are free (skipStripe), but outside AL they still need the
              tour agreement, and GA also needs the brokerage disclosure. */}
          <StripeContainer
            closeModal={() => modal.remove()}
            listing={listing}
            showingType={showingType}
            isFinalWalkthrough={isFinalWalkthrough}
          />
        </Elements>
        {/* )} */}
      </DialogContent>
    </Dialog>
  );
});

export default ScheduleTourModal;
